import { BaseEffect, EasingFunction, Easing, CanvasDimensions, EffectResult, MultiTrackEffect, LayerType } from './BaseEffect';

/**
 * PerLayerBlurEffect applies blur transformation using per-layer rendering.
 * 
 * ARCHITECTURE: Per-layer effect - each layer gets individual blur styling applied directly.
 * Better for performance and visual quality with blur effects.
 * 
 * This is ideal for blur effects because:
 * - Each layer can have different blur intensities
 * - Better GPU performance with individual filters
 * - No container nesting performance overhead
 * - More precise control over which layers are affected
 * 
 * @param from - Starting blur value in pixels (0 = no blur)
 * @param to - Ending blur value in pixels (0 = no blur)
 * @param duration - Duration in seconds
 * @param at - Start time in seconds
 * @param targetLayers - Array of layer types to apply the effect to
 * @param excludeLayers - Optional array of layer types to exclude from the effect
 * @param easing - Easing function
 */
export class PerLayerBlurEffect extends BaseEffect implements MultiTrackEffect {
    from: number; // blur value in pixels
    to: number;   // blur value in pixels
    targetLayers: LayerType[];
    excludeLayers: LayerType[];
    containerBased: boolean = false; // Per-layer: blur applied to individual layers for better performance

    constructor(config: {
        id?: string;
        startAt: number;
        duration: number;  
        blurLevel: number;
        trackTargets: string[];
    } | {
        from: number;
        to: number;
        duration: number;
        at: number;
        targetLayers?: LayerType[];
        excludeLayers?: LayerType[];
        easing?: EasingFunction;
    }) {
        // Handle both object formats - new object format and old individual parameter format
        let duration: number, at: number, from: number, to: number;
        let targetLayers: LayerType[], excludeLayers: LayerType[], easing: EasingFunction;
        
        if ('blurLevel' in config) {
            // New object format
            duration = config.duration;
            at = config.startAt;
            from = 0;
            to = config.blurLevel;
            targetLayers = ['background', 'scene', 'overlay', 'textOverlay'];
            excludeLayers = ['caption', 'branding'];
            easing = Easing.easeOutCubic;
            
            console.log(`[BLUR DEBUG] PerLayerBlurEffect NEW format: id=${config.id}, startAt=${config.startAt}, duration=${config.duration}, blurLevel=${config.blurLevel}`);
        } else {
            // Old individual parameter format (from factory methods)
            from = config.from;
            to = config.to;
            duration = config.duration;
            at = config.at;
            targetLayers = config.targetLayers || ['background', 'scene', 'overlay', 'textOverlay'];
            excludeLayers = config.excludeLayers || ['caption', 'branding'];
            easing = config.easing || Easing.easeOutCubic;
            
            console.log(`[BLUR DEBUG] PerLayerBlurEffect OLD format: from=${from}, to=${to}, duration=${duration}, at=${at}`);
        }
        
        super('per-layer-blur', duration, at, easing, true); // containerEffect = true
        
        // Validate and assign numeric values
        this.from = (typeof from === 'number' && !isNaN(from)) ? from : 0;
        this.to = (typeof to === 'number' && !isNaN(to)) ? to : 0;
        this.targetLayers = targetLayers;
        this.excludeLayers = excludeLayers || [];
        
        console.log(`[BLUR DEBUG] PerLayerBlurEffect final values: from=${this.from}, to=${this.to}, duration=${duration}, at=${at}`);
    }

    // Required by Effect interface - not used for multi-track effects but must be implemented
    apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult {
        // Per-layer effects don't use canvas operations - they use CSS filters
        return prev;
    }

    // Primary method for per-layer effects - returns CSS filter styles
    getContainerStyle(
        progress: number,
        dimensions?: CanvasDimensions,
        effectResult?: EffectResult
    ): React.CSSProperties {
        // Validate input values
        if (typeof progress !== 'number' || isNaN(progress)) {
            console.error(`[BLUR DEBUG] ${this.type} invalid progress:`, progress);
            return {};
        }
        
        if (typeof this.from !== 'number' || isNaN(this.from)) {
            console.error(`[BLUR DEBUG] ${this.type} invalid from value:`, this.from);
            return {};
        }
        
        if (typeof this.to !== 'number' || isNaN(this.to)) {
            console.error(`[BLUR DEBUG] ${this.type} invalid to value:`, this.to);
            return {};
        }
        
        const currentBlur = this.from + (this.to - this.from) * progress;
        
        // Validate the calculated blur value
        if (typeof currentBlur !== 'number' || isNaN(currentBlur)) {
            console.error(`[BLUR DEBUG] ${this.type} calculated NaN blur: from=${this.from}, to=${this.to}, progress=${progress}, currentBlur=${currentBlur}`);
            return {};
        }
        
        // Enhanced debug logging - always enabled for debugging blur issues
        console.log(`[BLUR DEBUG] ${this.type} getContainerStyle: progress=${progress.toFixed(3)}, currentBlur=${currentBlur.toFixed(1)}px, from=${this.from}px, to=${this.to}px, at=${this.at}s, duration=${this.duration}s`);
        
        // Apply blur filter
        const styles = {
            filter: `blur(${currentBlur}px)`,
            transition: 'none' // Prevent CSS transitions from interfering with Remotion
        };
        
        console.log(`[BLUR DEBUG] ${this.type} returning styles:`, styles);
        
        return styles;
    }

    // Helper method to check if a layer should be affected by this effect
    shouldApplyToLayer(layerType: LayerType): boolean {
        // If excludeLayers is specified and contains this layer, exclude it
        if (this.excludeLayers.includes(layerType)) {
            console.log(`[BLUR DEBUG] ${this.type} excluding layer: ${layerType}`);
            return false;
        }
        
        // If targetLayers is specified, only apply to listed layers
        const shouldApply = this.targetLayers.includes(layerType);
        console.log(`[BLUR DEBUG] ${this.type} layer ${layerType}: shouldApply=${shouldApply}, targetLayers=[${this.targetLayers.join(', ')}]`);
        return shouldApply;
    }

    // Static factory methods for common use cases

    /**
     * Creates blur effect excluding captions and branding - perfect for scene transitions
     * where you want to keep text readable while blurring the visual content
     */
    static createExcludingCaptions(
        from: number,
        to: number,
        duration: number,
        at: number,
        easing?: EasingFunction
    ): PerLayerBlurEffect {
        console.log(`[BLUR DEBUG] createExcludingCaptions RECEIVED: from=${from}, to=${to}, duration=${duration}, at=${at}, easing=${easing}`);
        
        return new PerLayerBlurEffect({
            from,
            to,
            duration,
            at,
            targetLayers: ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay'],
            excludeLayers: ['caption', 'branding'],
            easing
        });
    }

    /**
     * Creates blur effect for main content only - excludes overlays and text
     * Good for subtle background blurring while keeping UI elements sharp
     */
    static createMainContentOnly(
        from: number,
        to: number,
        duration: number,
        at: number,
        easing?: EasingFunction
    ): PerLayerBlurEffect {
        return new PerLayerBlurEffect({
            from,
            to,
            duration,
            at,
            targetLayers: ['background', 'scene', 'overlay'],
            excludeLayers: ['overlayTransition', 'textOverlay', 'caption', 'branding'],
            easing
        });
    }

    /**
     * Creates blur effect for all content except branding
     * Maximum blur impact while preserving brand elements
     */
    static createAllExceptBranding(
        from: number,
        to: number,
        duration: number,
        at: number,
        easing?: EasingFunction
    ): PerLayerBlurEffect {
        return new PerLayerBlurEffect({
            from,
            to,
            duration,
            at,
            targetLayers: ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay', 'caption'],
            excludeLayers: ['branding'],
            easing
        });
    }

    /**
     * Creates a subtle blur effect for scene transitions
     * Quick blur in/out with moderate intensity
     */
    static createSceneTransition(
        duration: number,
        at: number,
        maxBlur: number = 10,
        easing?: EasingFunction
    ): PerLayerBlurEffect {
        return new PerLayerBlurEffect({
            from: 0,
            to: maxBlur,
            duration,
            at,
            targetLayers: ['background', 'scene', 'overlay', 'overlayTransition'],
            excludeLayers: ['textOverlay', 'caption', 'branding'],
            easing: easing || Easing.easeInOutCubic
        });
    }
}

// Export with old name for backward compatibility during transition
export { PerLayerBlurEffect as MultiTrackBlurEffect };